import { Link } from 'react-router-dom';
import { BookOpen, Users, Package, Star, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const Index = () => {
  const { user, isStaff } = useAuth();
  const { t } = useTranslation('auth');

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.08),transparent_50%)]" />

        <nav className="relative z-10 container mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl primary-gradient flex items-center justify-center shadow-soft">
              <BookOpen className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-display text-xl font-bold text-foreground">{t('branding.appName')}</span>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <>
                {isStaff && (
                  <Link to="/staff">
                    <Button variant="ghost">{t('landing.nav.staffPortal')}</Button>
                  </Link>
                )}
                <Link to="/portal">
                  <Button>{t('landing.nav.myAccount')}</Button>
                </Link>
              </>
            ) : (
              <Link to="/auth">
                <Button>{t('landing.nav.getStarted')}</Button>
              </Link>
            )}
          </div>
        </nav>

        <div className="relative z-10 container mx-auto px-4 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            {t('landing.hero.badge')}
          </div>

          <h1 className="text-5xl md:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
            {t('landing.hero.titleLine1')}<br />
            <span className="text-primary">{t('landing.hero.titleLine2')}</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            {t('landing.hero.description')}
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="text-lg px-8">
                {t('landing.hero.ctaPrimary')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="text-lg px-8">
              {t('landing.hero.ctaSecondary')}
            </Button>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-background shadow-card">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-6">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-display font-bold text-foreground mb-3">
                {t('landing.features.tellUs.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('landing.features.tellUs.description')}
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-background shadow-card">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10 text-accent mb-6">
                <Package className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-display font-bold text-foreground mb-3">
                {t('landing.features.curate.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('landing.features.curate.description')}
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-background shadow-card">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-success/10 text-success mb-6">
                <Star className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-display font-bold text-foreground mb-3">
                {t('landing.features.readReturn.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('landing.features.readReturn.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl primary-gradient p-12 md:p-16 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4">
                {t('landing.cta.title')}
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8 max-w-xl mx-auto">
                {t('landing.cta.description')}
              </p>
              <Link to="/auth">
                <Button size="lg" variant="secondary" className="text-lg px-8">
                  {t('landing.cta.button')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg primary-gradient flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-display font-bold text-foreground">{t('branding.appName')}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
